import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Stack, Alert, FormControl, InputLabel,
  Select, MenuItem, Typography, Box,
} from '@mui/material';
import { bugReportsApi, type BugReportSeverity } from '../api/services';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Bug-report / support dialog. Triggered by the Support button in the app
 * shell. Submits to /api/bug-reports where the platform-admin triage tray
 * (PlatformBugReportsPage) picks it up. Captures the current page URL and
 * browser UA automatically so the user doesn't have to write them.
 *
 * Submission outcomes:
 * - Success → toast-style success Alert visible for 4s, then dialog closes
 *   and the form is reset.
 * - Failure → Alert stays on the form so the user can retry with the same
 *   text.
 */
export default function SupportDialog({ open, onClose }: Props) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<BugReportSeverity>('Medium');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setTitle('');
    setDescription('');
    setSeverity('Medium');
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      setError('A short title and a description are both required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await bugReportsApi.submit({
        title: title.trim(),
        description: description.trim(),
        severity,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      });
      setSuccess(true);
      setTimeout(() => {
        reset();
        onClose();
      }, 1800);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not send your report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Report a problem</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Describe what went wrong and what you were trying to do. The platform team
          gets these directly. We'll capture the page URL and browser automatically —
          you don't need to.
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Thanks — your report is in. The platform team will review it and reach
            out if they need more from you.
          </Alert>
        )}
        {error && !success && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          <TextField
            label="Short title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Calendar sync crashed when I clicked Replan"
            fullWidth
            disabled={submitting || success}
            autoFocus
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label="What happened?"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Steps to reproduce, what you expected, what actually happened, anything else that might help."
            fullWidth
            multiline
            minRows={5}
            disabled={submitting || success}
            inputProps={{ maxLength: 4000 }}
          />
          <FormControl fullWidth size="small" disabled={submitting || success}>
            <InputLabel>How serious is this?</InputLabel>
            <Select
              label="How serious is this?"
              value={severity}
              onChange={e => setSeverity(e.target.value as BugReportSeverity)}
            >
              <MenuItem value="Low">Low — small annoyance, no rush</MenuItem>
              <MenuItem value="Medium">Medium — affects how I work but I have a workaround</MenuItem>
              <MenuItem value="High">High — blocking me from getting today's work done</MenuItem>
              <MenuItem value="Critical">Critical — losing data or affecting my whole team</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              We'll attach automatically: <code>{window.location.pathname}</code> and your browser version.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || success || !title.trim() || !description.trim()}
        >
          {submitting ? 'Sending…' : 'Send report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
